import { buildCustomAttributeJsonPath } from './custom-attribute.paths';
import { isCustomAttributeType } from './custom-attribute.types';
/** Write cast per type — everything but arrays and booleans is stored as JSON text. */
const WRITE_CAST = {
    short_text: '::text',
    multi_line_text: '::text',
    url: '::text',
    // there is no numeric write cast, so numbers land in the blob as text (same as the portal).
    number: '::text',
    checkbox: '::boolean',
    dropdown: '::text',
    multi_select: '::text[]',
    date: '::text',
    date_time: '::text',
    lookup_relational: '::text',
};
const CLEAR = { ok: true, value: null };
/**
 * "No value", for every type alike: `null`/`undefined`, a string that is blank once trimmed, or an
 * empty array. Deciding this once — before the per-type parsers run — is what keeps `""` from
 * clearing one type while being rejected by another.
 */
function isEmptyInput(input) {
    if (input === null || input === undefined) {
        return true;
    }
    if (typeof input === 'string') {
        return input.trim().length === 0;
    }
    return Array.isArray(input) && input.length === 0;
}
/**
 * The write cast for an entry. A definition carrying an unknown type never reaches a write (
 * {@link parseCustomAttributeValue} rejects it first), so text is a safe last resort.
 */
function writeCastFor(entry) {
    return WRITE_CAST[isCustomAttributeType(entry.type) ? entry.type : 'short_text'];
}
/**
 * Validates one public input value against its definition and returns the JSON-storable form.
 * Anything {@link isEmptyInput} calls empty resolves to `null`, which the write path turns into a
 * key removal. Messages are public-facing and carry no key: callers prefix them with the attribute
 * the value belongs to.
 */
export function parseCustomAttributeValue(entry, input) {
    if (!isCustomAttributeType(entry.type) || entry.type === 'lookup_relational') {
        return { ok: false, message: 'this attribute type is not writable' };
    }
    if (isEmptyInput(input)) {
        return CLEAR;
    }
    switch (entry.type) {
        case 'short_text':
        case 'multi_line_text':
            return parseText(input);
        case 'url':
            return parseUrl(input);
        case 'number':
            return parseNumber(input);
        case 'checkbox':
            return typeof input === 'boolean'
                ? { ok: true, value: input }
                : { ok: false, message: 'expected a boolean' };
        case 'dropdown':
            return parseOptionKey(input, entry.options);
        case 'multi_select':
            return parseOptionKeys(input, entry.options);
        case 'date':
        case 'date_time':
            return parseDate(input);
    }
}
/**
 * Reads one value out of the stored blob into the form a caller sees: a number for `number` (the
 * blob keeps it as text — see {@link WRITE_CAST}), a boolean for `checkbox`, an array for
 * `multi_select`, a string for the rest. Decoding is {@link parseCustomAttributeValue} run against
 * stored input instead of request input — the writer already accepts every type's stored
 * representation, so the two cannot drift apart.
 *
 * Where reading deliberately parts ways with writing is the failing case. A write rejects input it
 * cannot store; a read has nothing to reject — the value is already ours. A definition that changed
 * under an existing blob (an option renamed or removed, a property switched to another type) leaves
 * values that no longer decode, and dropping them would report "not set" for data that is very much
 * set, which no caller could tell apart or act on. So such a value is passed through as stored,
 * narrowed only to the JSON shapes the contract allows. `undefined` therefore means one thing
 * only: nothing is stored, or what is stored is empty ({@link isEmptyInput}).
 *
 * Types the caller does not expose (`lookup_relational`, or a type this build does not know) are
 * the loader's business, not this function's — it decodes whatever entry it is handed.
 */
export function readCustomAttributeValue(entry, stored) {
    const parsed = parseCustomAttributeValue(entry, stored);
    if (parsed.ok) {
        // `null` is the empty case — see `isEmptyInput`.
        return parsed.value ?? undefined;
    }
    return toReadableValue(stored);
}
/**
 * A value the definition no longer describes, in a shape a JSON response can carry: scalars as they
 * are, arrays flattened to strings, anything else (an object, a nested structure no write path
 * produces) treated as absent.
 */
function toReadableValue(stored) {
    if (typeof stored === 'string' ||
        typeof stored === 'number' ||
        typeof stored === 'boolean') {
        return stored;
    }
    return Array.isArray(stored) ? stored.map(String) : undefined;
}
/**
 * Turns a parsed value into the `dynamicAttributes` entry the entity update path consumes. A `null`
 * value is passed through — the db layer removes the key instead of writing it.
 */
export function toDynamicAttributeWrite(entry, value) {
    return { attribute: buildCustomAttributeJsonPath(entry.key, writeCastFor(entry)), value };
}
/**
 * The value as it lands in the JSONB blob: an array for `multi_select`, a boolean for `checkbox`,
 * text for everything else — numbers included, since there is no numeric write cast (see
 * {@link WRITE_CAST}). A per-key write gets this for free from its cast; a caller writing the whole
 * column at once must apply it, otherwise the same property would be stored as a JSON number by
 * one path and a string by the other.
 */
export function toStoredCustomAttributeValue(entry, value) {
    switch (writeCastFor(entry)) {
        case '::text[]':
            return Array.isArray(value) ? value.map(String) : [String(value)];
        case '::boolean':
            return Boolean(value);
        default:
            return String(value);
    }
}
// the definition default (typed unknown) to the wire string the backend interprets per cast. only
// scalar defaults are forwarded; null/undefined and non-scalars (e.g. a multi_select's array
// default) yield undefined so no coalesce is emitted — the array read expression treats an absent
// value as NULL, which is the correct "empty" for overlap and is_empty/is_not_empty.
export function serializeCustomAttributeDefault(defaultValue) {
    if (typeof defaultValue === 'boolean')
        return defaultValue ? 'true' : 'false';
    if (typeof defaultValue === 'number') {
        return Number.isFinite(defaultValue) ? String(defaultValue) : undefined;
    }
    if (typeof defaultValue === 'string') {
        return defaultValue.length > 0 ? defaultValue : undefined;
    }
    return undefined;
}
function parseText(input) {
    return typeof input === 'string'
        ? { ok: true, value: input }
        : { ok: false, message: 'expected a string' };
}
function parseUrl(input) {
    if (typeof input !== 'string') {
        return { ok: false, message: 'expected an http(s) URL' };
    }
    try {
        const { protocol } = new URL(input);
        if (protocol !== 'http:' && protocol !== 'https:') {
            return { ok: false, message: 'expected an http(s) URL' };
        }
    }
    catch {
        return { ok: false, message: 'expected an http(s) URL' };
    }
    return { ok: true, value: input };
}
function parseNumber(input) {
    // a stored value arrives as text (see WRITE_CAST), so a numeric string is as valid as a number.
    const value = typeof input === 'string' ? Number(input.trim()) : input;
    return typeof value === 'number' && Number.isFinite(value)
        ? { ok: true, value }
        : { ok: false, message: 'expected a number' };
}
function parseOptionKey(input, options) {
    return typeof input === 'string' && options.some((option) => option.key === input)
        ? { ok: true, value: input }
        : { ok: false, message: `expected one of: ${listOptionKeys(options)}` };
}
function parseOptionKeys(input, options) {
    const invalid = {
        ok: false,
        message: `expected an array of: ${listOptionKeys(options)}`,
    };
    if (!Array.isArray(input)) {
        return invalid;
    }
    const known = new Set(options.map((option) => option.key));
    return input.every((item) => typeof item === 'string' && known.has(item))
        ? { ok: true, value: input }
        : invalid;
}
function parseDate(input) {
    if (typeof input !== 'string') {
        return { ok: false, message: 'expected an ISO 8601 date' };
    }
    const timestamp = Date.parse(input);
    return Number.isNaN(timestamp)
        ? { ok: false, message: 'expected an ISO 8601 date' }
        : { ok: true, value: new Date(timestamp).toISOString() };
}
function listOptionKeys(options) {
    return options
        .map((option) => option.key)
        .sort()
        .join(', ');
}
